<template>
  <div dir="rtl" class="h-screen overflow-hidden flex flex-col bg-gray-100">
    <!-- Header -->
    <div class="h-[12vh] flex justify-center flex-col items-center pt-[1vh] relative shrink-0">
      <h1 class="text-[2vw] font-bold tracking-wide absolute">النظـارات الطبيـة قيـد العمـل</h1>
      <div class="mt-[3vh] bg-[#BBC8D6] w-[30vw] h-[1.1vh]"></div>
      <div class="bg-[#345E83] w-[52vw] h-[1.1vh]"></div>
    </div>

    <!-- Three Columns -->
    <div class="flex-1 grid grid-cols-3 gap-[2vw] px-[2vw] pb-[1vw]" style="min-height: calc(100vh - 12vh - 10vh);">
      <!-- Waiting Column (Right) -->
      <div class="flex flex-col shadow-lg rounded-[1.5vw] overflow-hidden h-[76vh] bg-white">
        <div class="bg-[#FEAB57] rounded-[1vw] p-[0.9vw] mb-[0.5vh] shrink-0">
          <div class="flex items-center justify-between text-white">
            <div class="flex items-center gap-[0.5vw]">
              <Icon icon="mdi:clock-outline" :width="headerIconSize" :height="headerIconSize" />
              <span class="text-[1.4vw] font-bold">في الانتظار</span>
            </div>
            <span class="text-[1.4vw] font-bold">Waiting</span>
          </div>
        </div>
        <div class="flex-1 overflow-hidden relative p-[0.5vw]">
          <div v-if="waitingJobs.length > 0" class="space-y-[0.3vh]" :class="{ 'auto-scroll-container': waitingJobs.length > scrollThreshold }">
            <div
                v-for="(job, index) in waitingJobs.length > scrollThreshold ? [...waitingJobs, ...waitingJobs] : waitingJobs"
                :key="`waiting-${job.id}-${index}`"
                class="bg-white rounded-[0.5vw] p-[0.5vw] flex items-center justify-between"
                :class="{ 'even-gray-bg': (index % 2) === 1 }"
            >
              <span v-if="settings.getValue('customers_names_to_be_viewed_glasses_screen') == 'full_name'" class="text-gray-800 font-semibold text-[1.2vw]">{{ waitingJobs.length > scrollThreshold ? (index % waitingJobs.length) + 1 : index + 1 }}. {{ job.customer?.name || 'Unknown' }}</span>
              <span v-if="settings.getValue('customers_names_to_be_viewed_glasses_screen') == 'only_first_two_names'" class="text-gray-800 font-semibold text-[1.2vw]">{{ waitingJobs.length > scrollThreshold ? (index % waitingJobs.length) + 1 : index + 1 }}. {{ String(job.customer?.name).split(" ").splice(0, 2).join(" ") || 'Unknown' }}</span>
              <span class="text-[#3161FF] font-bold text-[1.2vw]">{{ job.notes || '' }}</span>
            </div>
          </div>
          <div v-else class="text-center text-gray-400 py-[3vh] text-[1.8vw]">
          </div>
        </div>
      </div>

      <!-- In Progress Column (Middle) -->
      <div class="flex flex-col shadow-lg rounded-[1.5vw] overflow-hidden h-[76vh] bg-white">
        <div class="bg-[#FEEB47] rounded-[1vw] p-[0.9vw] mb-[0.5vh] shrink-0">
          <div class="flex items-center justify-between text-gray-800">
            <div class="flex items-center gap-[0.5vw]">
              <Icon icon="mdi:progress-wrench" :width="headerIconSize" :height="headerIconSize" />
              <span class="text-[1.4vw] font-bold">قيد الانجاز</span>
            </div>
            <span class="text-[1.4vw] font-bold">In Progress</span>
          </div>
        </div>
        <div class="flex-1 overflow-hidden relative p-[0.5vw]">
          <div v-if="inProgressJobs.length > 0" class="space-y-[0.3vh]" :class="{ 'auto-scroll-container': inProgressJobs.length > scrollThreshold }">
            <div
                v-for="(job, index) in inProgressJobs.length > scrollThreshold ? [...inProgressJobs, ...inProgressJobs] : inProgressJobs"
                :key="`progress-${job.id}-${index}`"
                class="bg-white rounded-[0.5vw] p-[0.5vw] flex items-center justify-between"
                :class="{ 'even-gray-bg': (index % 2) === 1 }"
            >
              <span v-if="settings.getValue('customers_names_to_be_viewed_glasses_screen') == 'full_name'" class="text-gray-800 font-semibold text-[1.2vw]">{{ inProgressJobs.length > scrollThreshold ? (index % inProgressJobs.length) + 1 : index + 1 }}. {{ job.customer?.name || 'Unknown' }}</span>
              <span v-if="settings.getValue('customers_names_to_be_viewed_glasses_screen') == 'only_first_two_names'" class="text-gray-800 font-semibold text-[1.2vw]">{{ inProgressJobs.length > scrollThreshold ? (index % inProgressJobs.length) + 1 : index + 1 }}. {{ String(job.customer?.name).split(" ").splice(0, 2).join(" ") || 'Unknown' }}</span>
              <span class="text-[#3161FF] font-bold text-[1.2vw]">{{ job.notes || '' }}</span>
            </div>
          </div>
          <div v-else class="text-center text-gray-400 py-[3vh] text-[1.8vw]">
          </div>
        </div>
      </div>

      <!-- Ready Column (Left) -->
      <div class="flex flex-col shadow-lg rounded-[1.5vw] overflow-hidden h-[76vh] bg-white">
        <div class="bg-[#74C066] rounded-[1vw] p-[0.9vw] mb-[0.5vh] shrink-0">
          <div class="flex items-center justify-between text-white">
            <div class="flex items-center gap-[0.5vw]">
              <Icon icon="lets-icons:check-fill" :width="headerIconSize" :height="headerIconSize" />
              <span class="text-[1.4vw] font-bold">جاهزة</span>
            </div>
            <span class="text-[1.4vw] font-bold">Ready</span>
          </div>
        </div>
        <div class="flex-1 overflow-hidden relative p-[0.5vw]">
          <div v-if="readyJobs.length > 0" class="space-y-[0.3vh]" :class="{ 'auto-scroll-container': readyJobs.length > scrollThreshold }">
            <div
                v-for="(job, index) in readyJobs.length > scrollThreshold ? [...readyJobs, ...readyJobs] : readyJobs"
                :key="`ready-${job.id}-${index}`"
                class="bg-white rounded-[0.5vw] p-[0.5vw] flex items-center justify-between"
                :class="{ 'even-gray-bg': (index % 2) === 1 }"
            >
              <span v-if="settings.getValue('customers_names_to_be_viewed_glasses_screen') == 'full_name'" class="text-gray-800 font-semibold text-[1.2vw]">{{ readyJobs.length > scrollThreshold ? (index % readyJobs.length) + 1 : index + 1 }}. {{ job.customer?.name || 'Unknown' }}</span>
              <span v-if="settings.getValue('customers_names_to_be_viewed_glasses_screen') == 'only_first_two_names'" class="text-gray-800 font-semibold text-[1.2vw]">{{ readyJobs.length > scrollThreshold ? (index % readyJobs.length) + 1 : index + 1 }}. {{ String(job.customer?.name).split(" ").splice(0, 2).join(" ")|| 'Unknown' }}</span>
              <span class="text-[#3161FF] font-bold text-[1.2vw]">{{ job.notes || '' }}</span>
            </div>
          </div>
          <div v-else class="text-center text-gray-400 py-[3vh] text-[1.8vw]">
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="h-[10vh] bg-gray-50/30 shrink-0 z-20">
      <div class="h-full p-[1.5vw] mx-[1vw] bg-gradient-to-r bg-[#D2DBE3] rounded-[1vw] flex justify-between items-center shadow-inner">
       <div class="relative flex items-center gap-[1vw]">
          <div class="text-white text-[1.6vw] bg-[#1D4C75] px-[2vw] py-[1vh] rounded-[0.8vw]" dir="ltr">
              {{ currentTime }}
          </div>
          <div class="text-[1.7vw] text-[#1D4C75] font-bold">
            {{ footerMessage }}
          </div>
       </div>
        <div class="flex gap-[1.5vw] items-center">
          <!-- <img :src="theckyLogoAlt" class="h-[4vh] w-auto" alt="Thecky Logo Alt"> -->
          <img :src="theckyLogo" class="h-[5vh] w-auto" alt="Thecky Logo">
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Icon } from '@iconify/vue'
import { useHTTP } from '@/plugins/http'
import GlassesJob from '@/models/customer/GlassesJob'
import theckyLogo from '@/assets/images/footer.svg'
import theckyLogoAlt from '@/assets/images/queue-thecky-logo-alt.png'
import { useSettingsStore } from '@/stores/settingsStore'

const { t } = useI18n({
  messages: {
    en: {
      noJobs: 'No jobs in this status'
    },
    ar: {
      noJobs: 'لا توجد طلبات'
    }
  }
})

const http = useHTTP()
const settings = useSettingsStore()
const allJobs = ref<GlassesJob[]>([])
const currentTime = ref('')
const footerMessage = ref('')
let refreshInterval: number
let timeInterval: number

// Configurable threshold for auto-scroll
const scrollThreshold = computed(() => {
  return Number(settings.getValue('glasses_monitor_scroll_threshold')) || 13
})

const waitingJobs = computed(() =>
  allJobs.value
    .filter(j => j.status === 'waiting')
    .sort((a, b) => a.position - b.position)
)

const inProgressJobs = computed(() =>
  allJobs.value
    .filter(j => j.status === 'in_progress')
    .sort((a, b) => a.position - b.position)
)

const readyJobs = computed(() =>
  allJobs.value
    .filter(j => j.status === 'ready')
    .sort((a, b) => a.position - b.position)
)

// Computed property for responsive icon sizing (1.2vw)
const iconSize = computed(() => {
  return Math.floor(window.innerWidth * 0.012)
})

// Computed property for header icon sizing (1.5vw)
const headerIconSize = computed(() => {
  return Math.floor(window.innerWidth * 0.015)
})

async function loadJobs() {
  try {
    const response = await http.get<any[]>(GlassesJob.ENDPOINT)
    allJobs.value = response.map(job => GlassesJob.from(job))
  } catch (error) {
    console.error('Error loading glasses jobs:', error)
  }
}

function updateTime() {
  const now = new Date()
  currentTime.value = now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
}

function updateFooterMessage() {
  footerMessage.value = settings.getValue('glasses_list_queue_footer') || 'نشكر لكم صبركم ...'
}

onMounted(() => {
  loadJobs()
  updateTime()
  updateFooterMessage()

  // Refresh jobs every 2 seconds
  refreshInterval = window.setInterval(loadJobs, 2000)

  // Update time every second
  timeInterval = window.setInterval(updateTime, 1000)
})

onUnmounted(() => {
  if (refreshInterval) clearInterval(refreshInterval)
  if (timeInterval) clearInterval(timeInterval)
})
</script>

<style scoped>
/* Continuous auto-scroll animation - only for lists exceeding threshold */
.auto-scroll-container {
  animation: scroll-up 25s linear infinite;
  display: flex;
  flex-direction: column;
}

@keyframes scroll-up {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-50%);
  }
}

/* Striped row background */
.even-gray-bg {
  background-color: rgb(243 244 246) !important;
}

/* Ensure smooth continuous scrolling */
.flex-1.overflow-hidden {
  position: relative;
}
</style>
